import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useSnapshot } from 'valtio';
import { unitsOfCompetencyState } from './UnitsOfCompetencyState';
import { UnitOfCompetency } from '@duke-hq/svc-api-db';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ChevronDown, ChevronRight, Info, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { UnitContextCategoryModal } from './UnitContextCategoryModal';

type FormFields = Pick<
  UnitOfCompetency,
  | 'id'
  | 'title'
  | 'code'
  | 'description'
  | 'work_placement'
  | 'nominalHours'
  | 'volumeOfLearningHours'
  | 'subjectFieldOfEducation'
  | 'vetFlag'
  | 'unitContextCategoryId'
>;

interface UnitContextCategory {
  id: string;
  categoryName: string;
  createdAt: Date;
}

interface LinkedCourse {
  id: string;
  title: string;
  identifier: string | null;
}

export function UnitsOfCompetencySettingsDrawer({
  refetch,
  data,
  courses,
}: {
  refetch: any;
  data: FormFields;
  courses: LinkedCourse[];
}) {
  const unitsOfCompetencySnap = useSnapshot(unitsOfCompetencyState);
  const toast = useToast();

  const { data: contextCategories, refetch: refetchCategories } =
    trpcAdmin.unitContextCategories.all.useQuery();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    handleSubmit,
    control,
    register,
    clearErrors,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitting, isLoading },
  } = useForm<FormFields>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      title: data.title,
      code: data.code,
      description: data.description,
      work_placement: data.work_placement,
      nominalHours: data.nominalHours,
      volumeOfLearningHours: data.volumeOfLearningHours,
      vetFlag: data.vetFlag,
      subjectFieldOfEducation: data.subjectFieldOfEducation,
      unitContextCategoryId: data.unitContextCategoryId,
    } as FormFields,
  });

  // Watch the value of vetFlag
  const vetFlag = watch('vetFlag', false);

  const updateUnitSettings = trpcAdmin.unitsOfCompetency.update.useMutation({
    onSuccess: () => {
      toast({
        title: t`Unit settings updated successfully.`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });

      refetch();
      unitsOfCompetencyState.closeSettingsDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to update unit settings.`,
        description: error.message,
        status: 'error',
        isClosable: true,
        duration: 7000,
      });
    },
  });

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    const updateData = {
      ...values,
      title: values.title.replace(/\s+/g, ' ').trim(),
      id: data.id,
    };

    await updateUnitSettings.mutateAsync(updateData);
  };

  return (
    <Drawer
      isOpen={unitsOfCompetencySnap.isSettingsDrawerOpen}
      placement="right"
      onClose={unitsOfCompetencyState.closeSettingsDrawer}
      closeOnOverlayClick={false}
      size="lg"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <Text textStyle="2xl" fontWeight="medium">
            <Trans>Settings</Trans>
          </Text>
        </DrawerHeader>

        <DrawerBody>
          <Box as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired isInvalid={!!errors.title}>
                <FormLabel>
                  <Trans>Title</Trans>
                </FormLabel>
                <Input
                  type="text"
                  autoComplete="Off"
                  placeholder="Title"
                  {...register('title', {
                    required: t`Title is required`,
                    validate: (value) => {
                      const normalized = value?.replace(/\s+/g, ' ').trim();
                      if (!normalized) {
                        return t`Unit title cannot be empty or only spaces`;
                      }
                      return true;
                    },
                    onChange: (e) => {
                      const cleaned = e.target.value.replace(/\s+/g, ' ');
                      setValue('title', cleaned, { shouldValidate: true });

                      if (errors.title) {
                        clearErrors('title');
                      }
                    },
                  })}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.code}>
                <FormLabel>
                  <Trans>Code</Trans>
                </FormLabel>
                <Input
                  type="text"
                  autoComplete="off"
                  spellCheck="false"
                  {...register('code', {
                    required: t`Unit code is required`,
                    validate: (value) => {
                      const trimmed = value?.trim();

                      if (trimmed && !/^[a-zA-Z0-9]+$/.test(trimmed)) {
                        return t`Only letters and numbers are allowed.`;
                      }

                      return true;
                    },
                    onChange: (e) => {
                      let value = e.target.value;

                      // Remove leading/trailing spaces *as the user types*
                      value = value.trim();

                      if (errors.code) {
                        clearErrors('code');
                      }

                      setValue('code', value);
                    },
                  })}
                />
                <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
              </FormControl>

              <VStack gap="8px" w="full" py="8px">
                <HStack w="full" justify="space-between" align="start" gap="10px">
                  <FormControl isInvalid={!!errors.nominalHours} isRequired={vetFlag}>
                    <FormLabel>
                      <Trans>Nominal hours</Trans>{' '}
                      <Tooltip
                        label={t`The supervised hours assigned to this unit by the training package.`}
                        placement="top"
                      >
                        <Box as="span" display="inline-block" verticalAlign="middle">
                          <Info size={14} />
                        </Box>
                      </Tooltip>
                    </FormLabel>
                    <NumberInput
                      min={0}
                      max={500}
                      step={5}
                      defaultValue={getValues('nominalHours') || ''}
                      onChange={(valueAsNumber) => {
                        setValue('nominalHours', parseFloat(valueAsNumber), {
                          shouldValidate: true,
                        });
                      }}
                    >
                      <NumberInputField
                        {...register('nominalHours', {
                          valueAsNumber: true,
                          validate: (value) => {
                            if (
                              vetFlag &&
                              (Number.isNaN(value) || typeof value !== 'number' || value <= 0)
                            ) {
                              return t`Nominal hours are required when the unit is accredited.`;
                            }
                            return true;
                          },
                        })}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormErrorMessage>{errors.nominalHours?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.volumeOfLearningHours}>
                    <FormLabel>
                      <Trans>Volume of learning hours</Trans>{' '}
                      <Tooltip
                        label={t`Total learning hours including supervised, unsupervised, and assessment activities.`}
                        placement="top"
                      >
                        <Box as="span" display="inline-block" verticalAlign="middle">
                          <Info size={14} />
                        </Box>
                      </Tooltip>
                    </FormLabel>
                    <NumberInput
                      min={0}
                      step={5}
                      defaultValue={getValues('volumeOfLearningHours') ?? ''}
                      onChange={(valueAsNumber) => {
                        setValue('volumeOfLearningHours', parseInt(valueAsNumber, 10), {
                          shouldValidate: true,
                        });
                      }}
                    >
                      <NumberInputField
                        {...register('volumeOfLearningHours', {
                          valueAsNumber: true,
                          validate: (value) => {
                            if (value != null && typeof value === 'number' && value < 0) {
                              return t`Volume of learning hours must be zero or greater.`;
                            }
                            return true;
                          },
                        })}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormErrorMessage>{errors.volumeOfLearningHours?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
              </VStack>

              <FormControl
                isInvalid={!!errors.subjectFieldOfEducation}
                flex="1"
                isRequired={vetFlag}
              >
                <FormLabel>
                  <Trans>Subject of education</Trans>
                </FormLabel>
                <Input
                  type="text"
                  autoComplete="Off"
                  maxLength={6}
                  {...register('subjectFieldOfEducation', {
                    validate: (value) => {
                      const trimmed = value?.trim();

                      if (vetFlag && !trimmed) {
                        return t`Subject of education is required when the unit is accredited.`;
                      }

                      if (trimmed && !/^[a-zA-Z0-9]{6}$/.test(trimmed)) {
                        return t`Subject of education must be a 6-character alphanumeric string.`;
                      }

                      return true;
                    },
                  })}
                />
                <FormErrorMessage>{errors.subjectFieldOfEducation?.message}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="description">
                  <Trans>Description</Trans>
                </FormLabel>
                <Textarea
                  id="description"
                  autoComplete="off"
                  spellCheck="false"
                  size="sm"
                  {...register('description')}
                />
              </FormControl>

              <FormControl id="unitContextCategoryId">
                <FormLabel htmlFor="unitContextCategoryId">
                  <Trans>Context category</Trans>
                </FormLabel>
                <Controller
                  name="unitContextCategoryId"
                  control={control}
                  render={({ field }) => {
                    const selectedOption = contextCategories?.find(
                      (category) => category.id === field.value,
                    );

                    return (
                      <>
                        <Menu matchWidth>
                          <MenuButton
                            as={Button}
                            variant="outline"
                            px={2}
                            width="full"
                            colorScheme="gray"
                            textAlign="left"
                            rightIcon={<ChevronDown />}
                          >
                            {selectedOption ? selectedOption.categoryName : t`Please select`}
                          </MenuButton>
                          <MenuList maxH="300px" overflowY="auto" paddingBottom="0">
                            {contextCategories?.map((category) => (
                              <MenuItem
                                key={category.id}
                                onClick={() => field.onChange(category.id)}
                              >
                                {category.categoryName}
                              </MenuItem>
                            ))}
                            <MenuItem
                              onClick={() => setIsModalOpen(true)}
                              icon={<Plus />}
                              position="sticky"
                              bottom="0"
                              zIndex="1"
                              boxShadow="sm"
                            >
                              <Trans>Add or update categories</Trans>
                            </MenuItem>
                          </MenuList>
                        </Menu>

                        <UnitContextCategoryModal
                          isOpen={isModalOpen}
                          onClose={() => setIsModalOpen(false)}
                          contextCategories={contextCategories as UnitContextCategory[]}
                          refetchCategories={refetchCategories}
                        />
                      </>
                    );
                  }}
                />
              </FormControl>

              <FormControl>
                <HStack alignItems="center">
                  <FormLabel>
                    <Trans>Is this unit of competency accredited?</Trans>{' '}
                    <Tooltip
                      label={t`When on, this unit is included in AVETMISS reports. Turn off for non-vocational units.`}
                      placement="top"
                    >
                      <Box as="span" display="inline-block" verticalAlign="middle">
                        <Info size={14} />
                      </Box>
                    </Tooltip>
                  </FormLabel>
                  <Switch {...register('vetFlag')} colorScheme="brand" />
                </HStack>
              </FormControl>

              <FormControl>
                <Flex gap="4" align="baseline">
                  <FormLabel>
                    <Trans>Work placement required</Trans>
                  </FormLabel>
                  <Switch {...register('work_placement')} colorScheme="brand" />
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel pb={2}>
                  <Plural
                    value={courses.length}
                    _0="This unit is not used in any courses."
                    one="This unit is used in # course:"
                    other="This unit is used in # courses:"
                  />
                </FormLabel>
                {courses.length > 0 ? (
                  <VStack
                    align="stretch"
                    spacing={0}
                    borderWidth="1px"
                    borderColor="border.default"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    {courses.map((course, index) => (
                      <Flex
                        key={course.id}
                        as={RouterLink}
                        to={`/courses/${course.id}`}
                        w="full"
                        align="center"
                        justify="space-between"
                        gap={3}
                        px={3}
                        py={2}
                        borderTopWidth={index === 0 ? 0 : '1px'}
                        borderColor="border.default"
                        _hover={{ bg: 'bg.muted' }}
                        onClick={() => unitsOfCompetencyState.closeSettingsDrawer()}
                      >
                        <HStack gap={2} minW={0}>
                          {course.identifier ? (
                            <Badge colorScheme="gray" variant="pill" flexShrink={0}>
                              {course.identifier}
                            </Badge>
                          ) : null}
                          <Text color="brand.500" fontSize="sm">
                            {course.title}
                          </Text>
                        </HStack>
                        <Box as="span" color="fg.muted" flexShrink={0} lineHeight={0}>
                          <ChevronRight size={iconSize.sm} />
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                ) : null}
              </FormControl>

              <Flex justify={'flex-end'}>
                <Button
                  right={0}
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={updateUnitSettings.isPending || isSubmitting || isLoading}
                  loadingText="Submitting"
                >
                  <Trans>Update</Trans>
                </Button>
              </Flex>
            </VStack>
          </Box>
        </DrawerBody>

        <DrawerFooter></DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
